// AdminClients — Clients & dossiers : fils de messagerie client ↔ cabinet (v5.21.0).
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { T, Avatar } from "./cabinetTheme";
import { getFounderToken } from "@/lib/founderApi";

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${getFounderToken()}` });

export const AdminClients = ({ slug, onChanged }) => {
  const [threads, setThreads] = useState(null);
  const [sel, setSel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const endRef = useRef(null);

  const loadThreads = useCallback(() => {
    axios.get(`${API}/api/v3/cabinet/${slug}/admin/threads`, { headers: hdr() })
      .then((r) => setThreads(r.data.threads)).catch(() => setThreads([]));
  }, [slug]);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const open = async (t) => {
    setSel(t);
    const r = await axios.get(`${API}/api/v3/cabinet/${slug}/admin/threads/${encodeURIComponent(t.client_phone)}/messages`, { headers: hdr() });
    setMessages(r.data.messages);
    loadThreads(); onChanged?.();
    setTimeout(() => endRef.current?.scrollIntoView(), 60);
  };
  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    try {
      const r = await axios.post(`${API}/api/v3/cabinet/${slug}/admin/threads/${encodeURIComponent(sel.client_phone)}/messages`, { text }, { headers: hdr() });
      setMessages((p) => [...p, r.data.message]);
      setTimeout(() => endRef.current?.scrollIntoView(), 60);
    } catch { toast.error("Envoi impossible"); }
  };

  if (sel) return (
    <div style={{ ...T.card, display: "flex", flexDirection: "column", height: "70vh" }} data-testid="admin-thread">
      <div style={{ display: "flex", gap: 9, alignItems: "center", paddingBottom: 10, borderBottom: "1px solid #F3F4F6" }}>
        <button onClick={() => { setSel(null); loadThreads(); }} style={{ ...T.back, width: 30, height: 30 }} data-testid="thread-back">←</button>
        <Avatar name={sel.client_name} size={32} color="#065F46" />
        <div>
          <div style={{ fontSize: 13, fontWeight: 800 }}>{sel.client_name || sel.client_phone}</div>
          <div style={{ ...T.sub, fontSize: 11 }}>{sel.client_phone}</div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 7, padding: "10px 0" }}>
        {messages.map((m) => (
          <div key={m.message_id} style={{
            maxWidth: "80%", padding: "8px 12px", fontSize: 12.5, lineHeight: 1.4,
            ...(m.from === "cabinet"
              ? { alignSelf: "flex-end", background: "#065F46", color: "#fff", borderRadius: "12px 12px 4px 12px" }
              : { alignSelf: "flex-start", background: "#F3F4F6", color: "#111827", borderRadius: "12px 12px 12px 4px" }),
          }} data-testid={`thread-msg-${m.from}`}>
            {m.text}
            <div style={{ fontSize: 9.5, opacity: 0.6, marginTop: 2 }}>{String(m.at).slice(11, 16)}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div style={{ display: "flex", gap: 7 }}>
        <input style={{ ...T.input, flex: 1 }} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Répondre au client…" data-testid="thread-input" />
        <button onClick={send} style={{ ...T.btnGreen, padding: "0 16px" }} data-testid="thread-send">Envoyer</button>
      </div>
    </div>
  );

  return (
    <div style={T.card} data-testid="admin-clients">
      <h2 style={T.h2}>Clients & dossiers — Messagerie</h2>
      <div style={{ display: "grid", gap: 0, marginTop: 6 }}>
        {(threads || []).map((t) => (
          <div key={t.client_phone} onClick={() => open(t)} role="button"
            style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F3F4F6", cursor: "pointer" }}
            data-testid={`client-thread-${t.client_phone.replace(/\D/g, "")}`}>
            <Avatar name={t.client_name} size={36} color="#065F46" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800 }}>{t.client_name || t.client_phone}</div>
              <div style={{ ...T.sub, fontSize: 11.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {t.last_from === "cabinet" ? "Vous : " : ""}{t.last_text}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ ...T.sub, fontSize: 10.5 }}>{String(t.last_at).slice(11, 16)}</div>
              {t.unread > 0 && <span style={{ display: "inline-block", minWidth: 17, padding: "1px 5px", borderRadius: 99, background: "#F97316", color: "#fff", fontSize: 10, fontWeight: 900, textAlign: "center", marginTop: 3 }} data-testid={`thread-unread-${t.client_phone.replace(/\D/g, "")}`}>{t.unread}</span>}
            </div>
          </div>
        ))}
        {threads !== null && threads.length === 0 && <div style={{ ...T.sub, paddingTop: 8 }}>Aucune conversation pour le moment.</div>}
        {threads === null && <div style={{ ...T.sub, paddingTop: 8 }}>Chargement…</div>}
      </div>
    </div>
  );
};
